import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid"; // Import Grid for layout
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { CircularProgress, MenuItem } from "@mui/material"; // Import CircularProgress and MenuItem

export default function IncentiveEditModal({ open, handleClose, input, setInput }) {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Reset errors when modal opens or input changes
  useEffect(() => {
    if (open) {
      setErrors({});
    }
  }, [open, input]); // Added input as a dependency to reset errors if input data changes while open

  // Helper function for individual field validation
  const validateField = (name, value) => {
    let error = "";
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    switch (name) {
      case "percentage":
        const parsedPercentage = parseFloat(value);
        if (isNaN(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100) {
          error = "Percentage must be between 0 and 100.";
        }
        break;
      case "description":
        if (!trimmedValue) {
          error = "Description is required.";
        } else if (trimmedValue.length < 10) { // Example: minimum 10 characters
          error = "Description must be at least 10 characters long.";
        }
        break;
      case "status":
        if (!trimmedValue) {
          error = "Status is required.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For type="number" fields, parse float value
    const processedValue = name === "percentage" ? (value === "" ? "" : parseFloat(value)) : value; // Allow empty string for number field
    setInput((prevState) => ({ ...prevState, [name]: processedValue }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, processedValue) }));
  };

  // Function to validate all fields before submission
  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    const percentageError = validateField("percentage", input.percentage);
    if (percentageError) { newErrors.percentage = percentageError; isValid = false; }

    const descriptionError = validateField("description", input.description);
    if (descriptionError) { newErrors.description = descriptionError; isValid = false; }

    const statusError = validateField("status", input.status);
    if (statusError) { newErrors.status = statusError; isValid = false; }

    setErrors(newErrors);
    return isValid;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire("Validation Error", "Please correct the errors in the form.", "error");
      return;
    }

    setLoading(true); // Start loading
    try {
      const payload = {
        ...input,
        percentage: parseFloat(input.percentage), // Ensure percentage is a number for API
      };

      const response = await api.put(`/incentives/${input.id}`, payload);

      if (response.data.message === "Incentive updated successfully") {
        Swal.fire("Success", response.data.message, "success");
        handleClose(); // Close modal and trigger refresh in parent
      } else {
        console.error("Server response on error:", response.data);
        // Handle specific server-side messages for business logic errors
        Swal.fire("Error", response.data.message || "Failed to update incentive.", "error");
      }
    } catch (err) {
      console.error("Error updating incentive:", err.response || err);
      Swal.fire("Error", "An unexpected error occurred while updating incentive.", "error");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm"> {/* MaxWidth set to 'sm' for consistency */}
      <form onSubmit={handleUpdate}>
        <DialogTitle>Edit Incentive</DialogTitle>
        <DialogContent dividers> {/* Added dividers for visual separation */}
          <MDBox pt={1}>
            <Grid container spacing={2}> {/* Use Grid for consistent spacing */}
              <Grid item xs={12}>
                <TextField
                  label="Percentage"
                  name="percentage"
                  value={input.percentage || ""} // Ensure value is controlled even if null/undefined
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  margin="dense"
                  required
                  error={!!errors.percentage}
                  helperText={errors.percentage}
                  inputProps={{ min: 0, max: 100, step: 0.01 }} // Allow decimal percentages
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={input.description || ""} // Ensure value is controlled
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={3}
                  required
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              {/* Status as a dropdown */}
              <Grid item xs={12}>
                <TextField
                  label="Role"
                  name="role"
                  value={input.role || ""} // Ensure value is controlled
                  onChange={handleChange}
                  fullWidth
                  margin="dense"
                  multiline
                  rows={3}
                  required
                  error={!!errors.description}
                  helperText={errors.description}
                  disabled
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose} color="secondary" disabled={loading}>
            Cancel
          </MDButton>
          <MDButton type="submit" color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Update"}
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}